import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Search, MapPin, Users, Clock, Loader2, X, ArrowRight,
  FlaskConical, Code, Cog, Calculator, Bot, BookOpen, Sparkles, Filter
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  science: { label: 'Science', color: 'from-rose-500 to-pink-400', bg: 'bg-rose-500', badge: 'bg-rose-100 text-rose-700 border-rose-200', icon: FlaskConical },
  technology: { label: 'Technology', color: 'from-blue-500 to-indigo-400', bg: 'bg-blue-500', badge: 'bg-blue-100 text-blue-700 border-blue-200', icon: Code },
  engineering: { label: 'Engineering', color: 'from-emerald-500 to-teal-400', bg: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: Cog },
  math: { label: 'Math', color: 'from-purple-500 to-violet-400', bg: 'bg-purple-500', badge: 'bg-purple-100 text-purple-700 border-purple-200', icon: Calculator },
  robotics: { label: 'Robotics', color: 'from-orange-500 to-amber-400', bg: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700 border-orange-200', icon: Bot },
};

export default function CatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [sections, setSections] = useState([]);
  const [campSections, setCampSections] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    gradeBand: searchParams.get('grade') || '',
    category: searchParams.get('category') || '',
    locationId: searchParams.get('location') || '',
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [coursesRes, sectionsRes, locationsRes, campsListRes, campSectionsRes] = await Promise.all([
          axios.get(`${API_URL}/api/courses/`, { params: { page_size: 5000 } }),
          axios.get(`${API_URL}/api/sections/`, { params: { page_size: 5000 } }),
          axios.get(`${API_URL}/api/locations/`, { params: { page_size: 200 } }),
          axios.get(`${API_URL}/api/camps/`, { params: { page_size: 200 } }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/camp-sections/`, { params: { page_size: 5000 } }).catch(() => ({ data: [] })),
        ]);
        setCourses(coursesRes.data?.results || coursesRes.data || []);
        setSections(sectionsRes.data?.results || sectionsRes.data || []);
        setLocations(locationsRes.data?.results || locationsRes.data || []);

        // For each camp we need its `days` array (day_number → course mapping).
        // The list endpoint doesn't include this so we fan out detail calls in
        // parallel. ~24 camps × 1 request ≈ 1s on a warm cache. Acceptable for
        // a catalog page that already loads ~80 courses + 100+ sections.
        const campsList = campsListRes.data?.results || campsListRes.data || [];
        const campDetails = await Promise.all(
          campsList
            .filter(c => c.active !== false)
            .map(c =>
              axios.get(`${API_URL}/api/camps/${c.id}/`).then(r => r.data).catch(() => null),
            ),
        );
        const campSecs = campSectionsRes.data?.results || campSectionsRes.data || [];
        // Store both for the filter pipeline.
        setCampSections({ details: campDetails.filter(Boolean), sections: campSecs });
      } catch (err) {
        console.error('Failed to fetch catalog data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.search) params.set('search', filters.search);
    if (filters.gradeBand) params.set('grade', filters.gradeBand);
    if (filters.locationId) params.set('location', filters.locationId);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  // Build a Map<courseId, Set<locationId>> of locations where each course has
  // an UPCOMING (start_date >= today) scheduled session — either via:
  //   • Regular after-school sections (sections[].course + .location)
  //   • Camp sessions: a CampSection at a location whose camp has a CampDay
  //     mapped to this course.
  // Used by the location filter so we only show courses that actually run at
  // the selected location, and by the catalog empty state.
  const courseLocationMap = (() => {
    const map = new Map();
    const todayIso = new Date().toISOString().slice(0, 10);
    const add = (courseId, locId) => {
      if (!courseId || !locId) return;
      const key = String(courseId);
      if (!map.has(key)) map.set(key, new Set());
      map.get(key).add(String(locId));
    };
    // Regular after-school sections.
    sections.forEach(s => {
      const startDate = s.start_date || '';
      const endDate = s.end_date || '';
      // Treat "upcoming" as starting in the future OR currently running.
      const isUpcoming = !startDate || endDate >= todayIso;
      if (!isUpcoming) return;
      add(s.course_id || s.course, s.location_id || s.location);
    });
    // Camp sessions: map camp_id → set of locations from camp-sections, then
    // expand to each course on that camp's days.
    const campSecs = campSections.sections || [];
    const campDetails = campSections.details || [];
    const campLocations = new Map();      // camp_id → Set<location_id> upcoming
    campSecs.forEach(cs => {
      const startDate = cs.start_date || '';
      const endDate = cs.end_date || '';
      const isUpcoming = !startDate || endDate >= todayIso;
      if (!isUpcoming) return;
      const cId = String(cs.camp);
      if (!campLocations.has(cId)) campLocations.set(cId, new Set());
      campLocations.get(cId).add(String(cs.location));
    });
    campDetails.forEach(camp => {
      if (!camp) return;
      const locs = campLocations.get(String(camp.id));
      if (!locs || locs.size === 0) return;
      (camp.days || []).forEach(day => {
        const cId = day.course || day.course_id;
        locs.forEach(locId => add(cId, locId));
      });
    });
    return map;
  })();

  const filteredCourses = courses.filter(course => {
    if (/^W\d+D\d+:/.test(course.title)) return false;
    if (course.active === false) return false;
    if (filters.search && !course.title.toLowerCase().includes(filters.search.toLowerCase()) && !course.description?.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.gradeBand && !course.grade_bands?.includes(filters.gradeBand)) return false;
    if (filters.category && course.category !== filters.category) return false;
    if (filters.locationId) {
      // Real-data location filter (Feb-2026 fix). Previously every non-Workshop
      // course passed this filter at every location because the old code
      // assumed all themed courses were available everywhere via summer camps —
      // they're not. Now we check the actual courseLocationMap built from both
      // regular sections and camp-day↔location data, so picking "Bednarcik" no
      // longer shows the full 80-course catalog.
      const allowed = courseLocationMap.get(String(course.id));
      if (!allowed || !allowed.has(String(filters.locationId))) return false;
    }
    return true;
  }).sort((a, b) => {
    // Default order (Feb 2026, per request): alphabetical by title so the
    // unfiltered list is predictable. Workshop courses are still pinned to
    // the bottom because they're a different product shape (one-off
    // intensives vs the standard catalog). Within each bucket the sort is
    // case-insensitive alphabetical — this naturally pairs the matching
    // K-2 + 3-8 variants of the same theme.
    const aW = a.title?.toLowerCase().startsWith('workshop:') ? 1 : 0;
    const bW = b.title?.toLowerCase().startsWith('workshop:') ? 1 : 0;
    if (aW !== bW) return aW - bW;
    const at = (a.title || '').toLowerCase();
    const bt = (b.title || '').toLowerCase();
    return at.localeCompare(bt);
  });

  const clearFilters = () => setFilters({ search: '', gradeBand: '', category: '', locationId: '' });
  const hasActiveFilters = Object.values(filters).some(v => v);

  // Category counts
  const categoryCounts = {};
  courses.filter(c => !/^W\d+D\d+:/.test(c.title) && c.active !== false).forEach(c => {
    categoryCounts[c.category] = (categoryCounts[c.category] || 0) + 1;
  });

  return (
    <div data-testid="catalog-page">
      {/* Hero */}
      <section className="relative py-10 bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847]">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="container-app relative z-10">
          <div className="max-w-2xl mb-6">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-white mb-2">Course Catalog</h1>
            <p className="text-white/60 text-sm">Explore our STEM.org-accredited programs across Science, Technology, Engineering, Math & Robotics — for grades K–8</p>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap gap-2 mb-5">
            <button onClick={() => setFilters({ ...filters, category: '' })}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${!filters.category ? 'bg-rsc-yellow text-rsc-navy shadow' : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/15'}`}>
              All Courses <span className="ml-1 text-xs opacity-70">{Object.values(categoryCounts).reduce((a, b) => a + b, 0)}</span>
            </button>
            {Object.entries(CATEGORY_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              const count = categoryCounts[key] || 0;
              if (count === 0) return null;
              return (
                <button key={key} onClick={() => setFilters({ ...filters, category: key })}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${filters.category === key ? 'bg-rsc-yellow text-rsc-navy shadow' : 'bg-white/10 text-white/60 hover:text-white hover:bg-white/15'}`}>
                  <Icon className="w-3.5 h-3.5" /> {cfg.label} <span className="text-xs opacity-70">{count}</span>
                </button>
              );
            })}
          </div>

          {/* Search & Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <Input className="pl-9 bg-white/10 border-white/15 text-white placeholder:text-white/30 h-10"
                placeholder="Search courses..."
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value })} />
            </div>
            <Select value={filters.gradeBand || 'all'} onValueChange={v => setFilters({ ...filters, gradeBand: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[140px] bg-white/10 border-white/15 text-white h-10">
                <Users className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="All Grades" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                <SelectItem value="K-2">K–2</SelectItem>
                <SelectItem value="3-5">Grades 3–5</SelectItem>
                <SelectItem value="6-8">Grades 6–8</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.locationId || 'all'} onValueChange={v => setFilters({ ...filters, locationId: v === 'all' ? '' : v })}>
              <SelectTrigger className="w-[200px] bg-white/10 border-white/15 text-white h-10">
                <MapPin className="w-3.5 h-3.5 mr-1.5 text-white/40" /><SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                {locations.filter(l => l.active).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" onClick={clearFilters} className="text-white/60 hover:text-white h-10">
                <X className="w-4 h-4 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Course Grid */}
      <section className="py-8 bg-gray-50 min-h-[50vh]">
        <div className="container-app">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-rsc-blue" /></div>
          ) : (() => {
            // Park-district short-circuit. Park districts handle registration
            // through their own channels (we don't take their bookings on this
            // site), so when a parent location-filters to one we replace the
            // catalog with a dedicated CTA → /park-district-camps.
            const selectedLoc = filters.locationId
              ? locations.find(l => String(l.id) === String(filters.locationId))
              : null;
            const isParkDistrict = /park district/i.test(selectedLoc?.name || '');
            if (isParkDistrict) {
              return (
                <div
                  className="text-center py-12 max-w-xl mx-auto"
                  data-testid="catalog-park-district-cta"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 mb-4">
                    <MapPin className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">
                    {selectedLoc.name} registers through their own portal
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Park-district camps and classes are listed on our partner
                    page — registration happens directly through the park
                    district&rsquo;s booking system. We&rsquo;ve pulled the
                    full list of seasons, age groups, and links together for
                    you here.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <Link to="/park-district-camps">
                      <Button className="bg-rsc-yellow text-rsc-navy hover:bg-rsc-yellow-bright font-bold rounded-full px-6" data-testid="park-district-cta-btn">
                        View Park District Camps <ArrowRight className="w-4 h-4 ml-1.5" />
                      </Button>
                    </Link>
                    <Button variant="outline" onClick={clearFilters} data-testid="park-district-clear-filters">
                      Browse Full Catalog
                    </Button>
                  </div>
                </div>
              );
            }
            if (filteredCourses.length === 0) {
              return (
                <div className="text-center py-12" data-testid="catalog-empty-state">
                  <Filter className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  {filters.locationId ? (
                    <>
                      <h3 className="text-lg font-semibold mb-2">No upcoming courses at this location</h3>
                      <p className="text-muted-foreground mb-4">
                        {selectedLoc?.name || 'This location'}{' '}
                        doesn&rsquo;t have any scheduled sessions yet. Try another
                        location, or clear the filter to see the full catalog.
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold mb-2">No courses found</h3>
                      <p className="text-muted-foreground mb-4">Try adjusting your filters</p>
                    </>
                  )}
                  <Button variant="outline" onClick={clearFilters} data-testid="catalog-empty-clear-filters">Clear Filters</Button>
                </div>
              );
            }
            return (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm text-muted-foreground">{filteredCourses.length} course{filteredCourses.length !== 1 ? 's' : ''} found</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredCourses.map((course, index) => {
                    const cfg = CATEGORY_CONFIG[course.category] || CATEGORY_CONFIG.technology;
                    const CatIcon = cfg.icon;
                    const courseSections = sections.filter(s => (s.course_id || s.course) === course.id);
                    const availableSections = courseSections.filter(s => s.enrolled_count < s.capacity);

                    return (
                      <Link key={course.id} to={`/courses/${course.id}`} className="group" data-testid={`course-card-${index}`}>
                      <div className="rounded-xl border bg-white overflow-hidden hover:shadow-lg transition-all hover:-translate-y-0.5 h-full flex flex-col">
                        {/* Course image or category gradient */}
                        <div className={`h-36 relative overflow-hidden ${!course.thumbnail_url ? `bg-gradient-to-br ${cfg.color}` : ''}`}>
                          {course.thumbnail_url ? (
                            <>
                              <img src={course.thumbnail_url} alt={course.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                            </>
                          ) : (
                            <>
                              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
                              <CatIcon className="absolute right-4 bottom-3 w-16 h-16 text-white/15" />
                            </>
                          )}
                          <div className="absolute left-3 bottom-3">
                            <Badge className="bg-white/20 text-white border-0 backdrop-blur-sm text-[11px]">{cfg.label}</Badge>
                          </div>
                        </div>

                        <div className="p-4 flex-1 flex flex-col">
                          {/* Badges — first row carries the category at a
                              glance so the card is identifiable even when
                              scrolled past the hero image. Color + label per
                              CATEGORY_CONFIG (Science=rose, Technology=blue,
                              Engineering=emerald, Math=purple, Robotics=amber). */}
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            <Badge
                              className={`text-[10px] px-1.5 py-0 inline-flex items-center gap-1 border ${cfg.badge}`}
                              data-testid={`course-category-badge-${index}`}
                            >
                              <CatIcon className="w-2.5 h-2.5" />
                              {cfg.label}
                            </Badge>
                            {course.grade_bands?.map(g => (
                              <Badge key={g} variant="outline" className="text-[10px] px-1.5 py-0">{g}</Badge>
                            ))}
                            {course.skill_level && (
                              <Badge className={`text-[10px] px-1.5 py-0 capitalize ${
                                course.skill_level === 'beginner' ? 'bg-green-50 text-green-700 border-green-200' :
                                course.skill_level === 'intermediate' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                'bg-purple-50 text-purple-700 border-purple-200'
                              }`}>{course.skill_level}</Badge>
                            )}
                          </div>

                          {/* Title */}
                          <h3 className="font-bold text-sm text-gray-900 group-hover:text-blue-600 transition-colors mb-1.5 line-clamp-2">
                            {course.title}
                          </h3>

                          {/* Description */}
                          <p className="text-xs text-gray-500 line-clamp-2 mb-3 flex-1">
                            {course.description}
                          </p>

                          {/* Footer — Feb-2026 rule: the catalog default CTA is
                              "View Schedules", NOT "Join waitlist". Showing
                              "Join waitlist" up-front falsely implied courses
                              were full, when in reality schedules just hadn't
                              been picked yet. Waitlist sign-up still lives on
                              the course detail page for sections that ARE
                              full at a specific location. */}
                          <div className="flex items-center justify-between pt-2.5 border-t border-gray-100">
                            {availableSections.length > 0 && (
                              <span className="text-xs text-emerald-600 font-semibold">
                                {availableSections.length} session{availableSections.length !== 1 ? 's' : ''} open
                              </span>
                            )}
                            <span
                              className="text-xs text-blue-600 font-semibold group-hover:underline flex items-center gap-0.5 ml-auto"
                              data-testid={`view-schedules-cta-${index}`}
                            >
                              View Schedules <ArrowRight className="w-3 h-3" />
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              </>
            );
          })()}
        </div>
      </section>
    </div>
  );
}
